// How a visit becomes a claim line — the one implementation.
//
// Three paths build claim lines: the billing wizard, resync, and the Monday
// run (`open-scheduled-runs`). Until 2026-10-19 each built them its own way,
// and they disagreed: the Monday run dated a line by its scheduled start while
// the wizard used the clock-in (so an overnight visit could land on a
// different day and a different authorisation); it kept zero-hour lines the
// wizard dropped; it valued a broken clock pair differently; it resolved
// catalog collisions differently; and it matched clients by name without
// saying so. All three now call this.
//
// No Supabase, no DOM — loading visits, clients, the catalog and the
// authorisation history is the caller's job. Edge functions import this
// through _shared/lib.

import { visitHoursOn } from '../visitHours.js';
import { billingCodeFor, visitRef } from '../billingCodeRules.js';
import { resolvePriorAuth } from '../priorAuth.js';
import { unitsFromHours } from '../edi/units.js';

const round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));

export const normName = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

/** Hours on a basis, rounded the way a claim line stores them. */
export const lineHours = (v, basis) => round2(visitHoursOn(v, basis));

/**
 * The date a line is billed under, which is also the date its authorisation
 * is resolved against: when the care started — the clock-in where there is
 * one, otherwise the scheduled start.
 */
export const serviceDateOf = (v) =>
  String(v?.actualStartDate || v?.scheduledStartDate || v?.startDate || '').slice(0, 10);

/**
 * Whether a visit may bill on this basis. A missed or cancelled visit never
 * bills, on any basis — that is not a setting. Everything else carrying a
 * charge rate bills if it is worth more than nothing: on `clocked` a visit
 * nobody clocked is worth zero and is left out rather than written as an
 * empty line.
 */
export const isBillableVisit = (v, basis) =>
  !!v && !v.removed && (Number(v.chargeRate) || 0) > 0 && lineHours(v, basis) > 0;

/** A client record that may never receive a claim: a test record or a duplicate. */
export const isClaimableClient = (c) =>
  !!c && !c.is_test && !c.superseded_by && c.current_status !== 'Duplicate';

/** The client fields a claim line and its authorisation need. */
const clientRecord = (c) => ({
  id: c.id,
  client_id: c.client_id || null,
  medicaid_id: c.medicaid_id || '',
  diagnosis_code: c.diagnosis_code || '',
  dma_80_pa_number: c.dma_80_pa_number || '',
  pa_expiry_date: c.pa_expiry_date || null,
  // The payer decides where the line files; recorded on the line and used to
  // split a week into one run per destination.
  payer: c.service_type || null,
});

/**
 * Which CAERA client each visit belongs to, keyed by AxisCare client id.
 *
 * Matched on AxisCare's numeric id against `clients.axiscare_client_id` — the
 * field staff actually populate (`visit.client.clientId` is an unused
 * free-text field and never a join key). Failing that, by name, marked
 * `viaName` so the wizard can show it for review. Test and duplicate records
 * are never candidates, and a name two clients share matches neither: a
 * duplicate record must not be able to capture a claim.
 *
 * @returns {Object<string, object>} axisId → client record ({} when unmatched)
 */
export const matchVisitClients = (visits = [], clients = []) => {
  const eligible = (clients || []).filter(isClaimableClient);
  const byAxisId = new Map();
  const byName = new Map();
  for (const c of eligible) {
    if (c.axiscare_client_id != null && c.axiscare_client_id !== '') {
      const k = String(c.axiscare_client_id);
      byAxisId.set(k, byAxisId.has(k) ? null : c);   // null = ambiguous
    }
    const n = normName(`${c.first_name || ''} ${c.last_name || ''}`);
    if (n) byName.set(n, byName.has(n) ? null : c);
  }
  const out = {};
  for (const v of visits || []) {
    const axisId = String(v?.client?.id ?? '');
    if (!axisId || out[axisId]) continue;
    const idMatch = byAxisId.get(axisId);
    if (idMatch) { out[axisId] = clientRecord(idMatch); continue; }
    const n = v.client ? normName(`${v.client.firstName || ''} ${v.client.lastName || ''}`) : '';
    const nameMatch = n ? byName.get(n) : null;
    out[axisId] = nameMatch ? { ...clientRecord(nameMatch), viaName: true } : {};
  }
  return out;
};

/** The client record a visit matched, or {}. */
export const clientOf = (v, clientMap = {}) => clientMap[String(v?.client?.id ?? '')] || {};

/**
 * One claim line for one visit, without its billing_run_id.
 *
 * @param {object} p
 * @param {object} p.visit        a visit from the unified feed (AxisCare-shaped)
 * @param {object} p.client       its matched client record (from matchVisitClients)
 * @param {object[]} p.authHistory  that client's prior_auth_history rows
 * @param {object[]} p.catalog    service_catalog rows
 * @param {string} p.basis        the run's hours basis
 * @param {number} p.multiplier   the run's charge multiplier
 * @param {string} [p.note]       appended to the line's notes in brackets
 */
export const buildClaimLine = ({ visit: v, client = {}, authHistory = [], catalog = [], basis, multiplier, note = '' }) => {
  const bc = billingCodeFor(v, catalog);
  const hours = lineHours(v, basis);
  const rate = Number(v.chargeRate) || 0;
  const date = serviceDateOf(v);
  const accrued = round2(hours * rate);
  const charge = round2(accrued * (Number(multiplier) || 0));
  return {
    client_id: client.id || null,
    client_name: v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '',
    medicaid_id: client.medicaid_id || '',
    payer: client.payer || null,
    service_date: date,
    service_code: bc.service_code,
    modifier: bc.modifier,
    service_label: bc.service_label,
    source: bc.source,
    caera_visit_id: bc.caera_visit_id,
    hours,
    // Bill one basis, record both. The gap between them is the hours
    // delivered-but-unclocked (or clocked-but-unscheduled), and it stays
    // answerable later without recomputing from visit data that has moved on.
    scheduled_hours: lineHours(v, 'scheduled'),
    clocked_hours: lineHours(v, 'clocked'),
    hours_basis: basis,
    units: unitsFromHours(hours),
    rate,
    accrued_amount: accrued,
    charge_amount: charge,
    gross_charge_amount: charge,
    status: 'approved',
    diagnosis_code: client.diagnosis_code || '',
    // The authorisation in force on this line's own service date. A claim
    // filed without one comes back CO-15.
    prior_auth_number: client.id ? (resolvePriorAuth({ history: authHistory, client, date }).number || '') : '',
    axiscare_visit_id: String(v.visitId ?? v.id ?? ''),
    notes: `${visitRef(v)} — ${date}${note ? ` [${note}]` : ''}`,
  };
};

/** A run's totals, summed from its stored (rounded) lines so the two always agree. */
export const runTotals = (lines = []) => ({
  lines: lines.length,
  accrued: round2(lines.reduce((s, l) => s + (Number(l.accrued_amount) || 0), 0)),
  charge: round2(lines.reduce((s, l) => s + (Number(l.charge_amount) || 0), 0)),
});
